import {
  Button,
  Checkbox,
  Css,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SelectField,
  TextField,
  useModal,
} from "@homebound/beam";
import { ReactNode, useState } from "react";
import { useController } from "src/hooks/useController";
import {
  FieldAttribution,
  UwConfigCreateRowEndpoint,
  UwConfigDeleteRowEndpoint,
  UwConfigOptions,
  UwConfigRow,
  UwConfigUpdateRowEndpoint,
  UwConfigValue,
} from "./UwConfigEndpoints";
import { useAfterUwConfigWrite } from "./useAfterUwConfigWrite";
import { FieldDef, FormTexts, formatValue, parseValue, sameValue, UwConfigTypeDef } from "./uwConfigFields";

type UwConfigRowModalProps = {
  typeDef: UwConfigTypeDef;
  /** Known values for the config's fields */
  options: UwConfigOptions;
  /** The row to edit, or undefined to create a new row */
  row?: UwConfigRow;
};

export function UwConfigRowModal({ typeDef, options, row }: UwConfigRowModalProps) {
  const { closeModal } = useModal();
  const { fetch } = useController();
  const afterWrite = useAfterUwConfigWrite();
  const [texts, setTexts] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      typeDef.fields.map((f) => [f.name, formatValue(f, row ? row[f.name] : typeDef.createDefaults?.[f.name])]),
    ),
  );
  const [booleans, setBooleans] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(typeDef.fields.filter((f) => f.kind === "boolean").map((f) => [f.name, !!row?.[f.name]])),
  );
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [saving, setSaving] = useState(false);

  const resolved = resolveTexts(typeDef.fields, options, texts);
  const parsed = Object.fromEntries(
    typeDef.fields.map((f) => [
      f.name,
      f.kind === "boolean"
        ? { value: booleans[f.name] }
        : parseChoice(f, options, resolved, row ? formatValue(f, row[f.name]) : undefined),
    ]),
  );
  const hasErrors = Object.values(parsed).some((p) => p.error);
  const changes: Record<string, UwConfigValue> = Object.fromEntries(
    typeDef.fields
      .filter((f) => !row || !sameValue(parsed[f.name].value, row[f.name]))
      .map((f) => [f.name, parsed[f.name].value as UwConfigValue]),
  );
  const changeCount = Object.keys(changes).length;

  async function run(action: () => Promise<unknown>, message: string) {
    setSaving(true);
    try {
      await action();
    } catch {
      // useController already showed the API's error message; keep the modal open so it can be corrected
      setSaving(false);
      return;
    }
    closeModal();
    await afterWrite(typeDef.type, message);
  }

  function onSave() {
    if (row) {
      return run(
        () => fetch(UwConfigUpdateRowEndpoint, { configType: typeDef.type, rowId: row.id, changes }),
        `Saved ${changeCount} change${changeCount === 1 ? "" : "s"}`,
      );
    }
    return run(
      () =>
        fetch(UwConfigCreateRowEndpoint, { configType: typeDef.type, row: { ...typeDef.createDefaults, ...changes } }),
      "Row added",
    );
  }

  function onDelete() {
    return run(() => fetch(UwConfigDeleteRowEndpoint, { configType: typeDef.type, rowId: row!.id }), "Row deleted");
  }

  return (
    <>
      <ModalHeader>{row ? `Edit ${typeDef.label}` : `Add ${typeDef.label}`}</ModalHeader>
      <ModalBody>
        <div css={Css.dg.gap2.$}>
          {typeDef.fields.map((f) =>
            f.kind === "boolean" ? (
              <div key={f.name}>
                <Checkbox
                  label={f.label}
                  selected={booleans[f.name]}
                  onChange={(selected) => setBooleans((b) => ({ ...b, [f.name]: selected }))}
                />
                {row && <div css={Css.xs.gray700.mt1.$}>{formatAttribution(row.field_updates[f.name])}</div>}
              </div>
            ) : (
              <FormField
                key={f.name}
                field={f}
                options={options}
                texts={resolved}
                onChange={(text) => setTexts((t) => ({ ...t, [f.name]: text }))}
                errorMsg={parsed[f.name].error}
                attribution={row?.field_updates[f.name]}
              />
            ),
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        {row &&
          (confirmingDelete ? (
            <div css={Css.df.aic.gap1.mra.$}>
              <span css={Css.sm.$}>Delete this row?</span>
              <Button variant="danger" label="Confirm Delete" onClick={onDelete} disabled={saving} />
              <Button variant="tertiary" label="Keep" onClick={() => setConfirmingDelete(false)} />
            </div>
          ) : (
            <div css={Css.mra.$}>
              <Button variant="tertiaryDanger" label="Delete" onClick={() => setConfirmingDelete(true)} />
            </div>
          ))}
        <Button variant="tertiary" label="Cancel" onClick={closeModal} />
        <Button
          label={row ? "Save" : "Add"}
          onClick={onSave}
          disabled={
            saving || (hasErrors ? "Fix the errors above" : row && changeCount === 0 ? "No changes to save" : false)
          }
        />
      </ModalFooter>
    </>
  );
}

/** The form's texts with derived fields filled in and disabled fields cleared to their null value */
function resolveTexts(fields: FieldDef[], options: UwConfigOptions, texts: FormTexts): FormTexts {
  const resolved = { ...texts };
  for (const f of fields) {
    if (f.derive) resolved[f.name] = f.derive(options, resolved);
    if (f.disabledReason?.(options, resolved)) resolved[f.name] = f.nullLabel ?? "";
  }
  return resolved;
}

function parseChoice(field: FieldDef, options: UwConfigOptions, texts: FormTexts, savedText: string | undefined) {
  const text = texts[field.name];
  const result = parseValue(field, text);
  // fields without "add new" (e.g. a variable after changing the section) must be one of the choices, unless it's the
  // saved value, which may no longer be offered (the API decides whether it's still allowed)
  const offered = !field.choices || field.allowNew || !text || text === savedText;
  if (!result.error && !offered && !field.choices!(options, texts).includes(text)) {
    return { error: `Pick a ${field.label.toLowerCase()} from the list` };
  }
  return result;
}

const ADD_NEW = "__add_new__";

type FormFieldProps = {
  field: FieldDef;
  options: UwConfigOptions;
  texts: FormTexts;
  onChange: (text: string) => void;
  errorMsg: string | undefined;
  attribution: FieldAttribution | undefined;
};

/** A text input, or a dropdown of known values (optionally with "Add new…" to type one) for fields with choices */
function FormField({ field, options, texts, onChange, errorMsg, attribution }: FormFieldProps) {
  const value = texts[field.name];
  const known = field.choices ? [...(field.nullLabel ? [field.nullLabel] : []), ...field.choices(options, texts)] : [];
  const [typingNew, setTypingNew] = useState(() => !!field.allowNew && value !== "" && !known.includes(value));
  const disabledReason = field.disabledReason?.(options, texts);
  const readOnly = field.readOnly || !!field.derive;
  const helperText = joinHelperText([
    !field.choices && !readOnly && field.nullLabel && `Leave blank for ${field.nullLabel}`,
    field.kind === "integerList" && "Comma separated",
    field.helperText,
    attribution && formatAttribution(attribution),
  ]);

  // a disabled field (e.g. a ready plan outside per-plan sections) shows the disabled dropdown, even mid "add new"
  if (!field.choices || readOnly || (typingNew && !disabledReason)) {
    return (
      <div>
        <TextField
          label={field.label}
          value={value}
          onChange={(text) => onChange(text ?? "")}
          readOnly={readOnly}
          errorMsg={errorMsg}
          helperText={helperText}
        />
        {typingNew && (
          <Button
            variant="text"
            label="Choose from list"
            onClick={() => {
              setTypingNew(false);
              onChange("");
            }}
          />
        )}
      </div>
    );
  }

  // keep a current value that's no longer a known choice visible, so it can be seen and changed
  const values = value && !known.includes(value) ? [value, ...known] : known;
  return (
    <SelectField
      label={field.label}
      value={value || undefined}
      onSelect={(selected) => {
        if (selected === ADD_NEW) {
          setTypingNew(true);
          onChange("");
        } else {
          onChange(selected ?? "");
        }
      }}
      options={[
        ...values.map((v) => ({ id: v, name: v })),
        ...(field.allowNew ? [{ id: ADD_NEW, name: `Add new ${field.label.toLowerCase()}…` }] : []),
      ]}
      disabled={disabledReason}
      errorMsg={errorMsg}
      helperText={disabledReason ? undefined : helperText}
    />
  );
}

function joinHelperText(parts: (string | false | undefined | null | "")[]): ReactNode {
  const shown = parts.filter(Boolean);
  return shown.length ? shown.join(" · ") : undefined;
}

export function formatAttribution(attribution: FieldAttribution | undefined): string {
  if (!attribution) return "";
  if (!attribution.updated_by) return "From the original config";
  return `Last changed by ${attribution.updated_by} on ${formatDate(attribution.updated_at)}`;
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
}
